import { z } from 'zod';

/**
 * AI-assisted issue analysis.
 *
 * Claude is given an alert (or ticket, or resource) plus read access to the
 * platform's own data and live diagnostics, and asked to work out *why*
 * something broke. The output is deliberately structured rather than prose:
 * a root cause an engineer can disagree with, the evidence it rests on, and
 * concrete next steps.
 *
 * `evidence` matters as much as `rootCause` — an unsourced diagnosis is a guess
 * with better grammar, and the tool calls are recorded so the reasoning can be
 * checked against what was actually observed.
 */

export const AI_SUBJECT_TYPES = ['alert', 'ticket', 'resource'] as const;
export type AiSubjectType = (typeof AI_SUBJECT_TYPES)[number];

export const AI_CONFIDENCE = ['low', 'medium', 'high'] as const;
export type AiConfidence = (typeof AI_CONFIDENCE)[number];

export interface AiToolCall {
  name: string;
  input: Record<string, unknown>;
  /** Trimmed for storage — the full result is not worth keeping. */
  resultPreview: string;
  ms: number;
}

export interface AiAnalysis {
  id: string;
  subjectType: AiSubjectType;
  subjectId: string;
  status: 'running' | 'complete' | 'failed';
  model: string;
  summary: string | null;
  rootCause: string | null;
  evidence: string[];
  remediation: string[];
  confidence: AiConfidence | null;
  toolCalls: AiToolCall[];
  inputTokens: number | null;
  outputTokens: number | null;
  cachedTokens: number | null;
  error: string | null;
  requestedBy: string | null;
  startedAt: number;
  finishedAt: number | null;
}

/** The shape Claude is asked to return, enforced with structured outputs. */
export const aiFindingSchema = z.object({
  summary: z.string().describe('One sentence an on-call engineer can read at a glance.'),
  rootCause: z.string().describe('The most likely underlying cause, stated plainly. Say so if the evidence is insufficient.'),
  evidence: z.array(z.string()).describe('Specific observations supporting the conclusion — metric values, timings, tool output.'),
  remediation: z.array(z.string()).describe('Concrete next steps, most useful first.'),
  confidence: z.enum(AI_CONFIDENCE).describe('How well the evidence supports the root cause.'),
});
export type AiFinding = z.infer<typeof aiFindingSchema>;

/**
 * JSON Schema mirror of `aiFindingSchema`, for providers that take structured
 * output as a raw schema (Claude's `output_config`, Ollama's `format`) rather
 * than a Zod object. Written out rather than derived: Zod 3 has no
 * `toJSONSchema`, and the schema is small enough that a converter dependency
 * would cost more than it saves.
 */
export const AI_FINDING_JSON_SCHEMA = {
  type: 'object',
  properties: {
    summary: { type: 'string', description: 'One sentence an on-call engineer can read at a glance.' },
    rootCause: {
      type: 'string',
      description: 'The most likely underlying cause, stated plainly. Say so if the evidence is insufficient.',
    },
    evidence: {
      type: 'array',
      items: { type: 'string' },
      description: 'Specific observations supporting the conclusion — metric values, timings, tool output.',
    },
    remediation: {
      type: 'array',
      items: { type: 'string' },
      description: 'Concrete next steps, most useful first.',
    },
    confidence: {
      type: 'string',
      enum: ['low', 'medium', 'high'],
      description: 'How well the evidence supports the root cause.',
    },
  },
  required: ['summary', 'rootCause', 'evidence', 'remediation', 'confidence'],
  additionalProperties: false,
} as const;

export const analyseRequestSchema = z.object({
  subjectType: z.enum(AI_SUBJECT_TYPES),
  subjectId: z.string(),
  /** Extra operator context: "started after the firewall upgrade last night" — long enough to paste in a log excerpt or config snippet. */
  hint: z.string().max(20000).optional(),
});
export type AnalyseRequest = z.infer<typeof analyseRequestSchema>;

/* ------------------------------------------------------------------ */
/* Provider settings                                                   */
/* ------------------------------------------------------------------ */

export const AI_PROVIDERS = ['anthropic', 'ollama'] as const;
export type AiProvider = (typeof AI_PROVIDERS)[number];

/** What the Settings page reads. The Anthropic key is never sent back — only whether one is set. */
export interface AiSettings {
  provider: AiProvider;
  hasAnthropicApiKey: boolean;
  anthropicWorkspaceId: string;
  ollamaBaseUrl: string;
  ollamaModel: string;
  updatedAt: number | null;
}

export const aiSettingsUpdateSchema = z.object({
  provider: z.enum(AI_PROVIDERS),
  // Omitted = leave the stored key untouched. Empty string is rejected here —
  // use clearAnthropicApiKey to remove it — so a blank field can never
  // silently wipe a working key on an unrelated save.
  anthropicApiKey: z.string().min(1).optional(),
  clearAnthropicApiKey: z.boolean().optional(),
  anthropicWorkspaceId: z.string().optional(),
  ollamaBaseUrl: z.string().optional(),
  ollamaModel: z.string().optional(),
});
export type AiSettingsUpdate = z.infer<typeof aiSettingsUpdateSchema>;

/* ------------------------------------------------------------------ */
/* AI chat                                                             */
/* ------------------------------------------------------------------ */

/**
 * Multi-turn chat, additive to the single-shot analysis above.
 *
 * A conversation can be bound to a subject (same alert/ticket/resource
 * framing as `analyse()`) or standalone. Every row in `ai_messages` is one
 * provider content block — not one message — because a single turn can carry
 * several tool_use blocks at once (parallel calls) or several tool_result
 * blocks at once (resuming several siblings together). Storing block-per-row
 * means the same rows project cleanly into either provider's wire format at
 * call time; nothing is stored twice.
 */

export const AI_MESSAGE_ROLES = ['user', 'assistant', 'tool'] as const;
export type AiMessageRole = (typeof AI_MESSAGE_ROLES)[number];

export const AI_BLOCK_TYPES = ['text', 'tool_use', 'tool_result'] as const;
export type AiBlockType = (typeof AI_BLOCK_TYPES)[number];

/**
 * `auto` — a read-only tool call, run immediately, never needs approval.
 * `pending` -> `approved`|`denied` -> `executed`|`failed` is the lifecycle of
 * a write-capable action tool call.
 */
export const AI_ACTION_STATUSES = ['auto', 'pending', 'approved', 'denied', 'executed', 'failed'] as const;
export type AiActionStatus = (typeof AI_ACTION_STATUSES)[number];

/** The fixed set of write-capable tools the chat model may request — see ai-actions.ts on the server. */
export const AI_ACTION_TOOL_NAMES = [
  'ack_alert',
  'resolve_alert',
  'recheck_resource',
  'comment_ticket',
  'update_ticket_state',
  'run_device_command',
] as const;
export type AiActionToolName = (typeof AI_ACTION_TOOL_NAMES)[number];

export interface AiMessage {
  id: string;
  conversationId: string;
  seq: number;
  role: AiMessageRole;
  blockType: AiBlockType;
  text: string | null;
  toolUseId: string | null;
  toolName: string | null;
  toolInput: Record<string, unknown> | null;
  toolResult: string | null;
  toolIsError: boolean | null;
  actionStatus: AiActionStatus | null;
  approvedBy: string | null;
  approvedAt: number | null;
  model: string | null;
  createdAt: number;
}

export interface AiConversation {
  id: string;
  subjectType: AiSubjectType | null;
  subjectId: string | null;
  title: string | null;
  status: 'active' | 'archived';
  requestedBy: string | null;
  createdAt: number;
  updatedAt: number;
}

/** Every conversation-mutating route returns this — the whole finished thing, same convention as `analyse()`. */
export interface AiConversationView {
  conversation: AiConversation;
  messages: AiMessage[];
}

export const createConversationSchema = z
  .object({
    subjectType: z.enum(AI_SUBJECT_TYPES).optional(),
    subjectId: z.string().optional(),
    message: z.string().min(1).max(20000),
  })
  .refine((v) => (v.subjectType == null) === (v.subjectId == null), {
    message: 'subjectType and subjectId must be provided together',
  });
export type CreateConversationRequest = z.infer<typeof createConversationSchema>;

export const postConversationMessageSchema = z.object({ message: z.string().min(1).max(20000) });
export type PostConversationMessageRequest = z.infer<typeof postConversationMessageSchema>;

export const conversationActionDecisionSchema = z.object({ decision: z.enum(['approve', 'deny']) });
export type ConversationActionDecision = z.infer<typeof conversationActionDecisionSchema>;
